<template>
    <div class="card border-1 mb-1 section-card">
        <div class="section-header">
            <div class="d-flex align-items-center">
                <span class="section-title">{{ title }}</span>
                <span class="row-count-badge">{{ plans.length }}</span>
            </div>
            <a v-if="showCreate" href="javascript:void(0)" class="fw-bold" @click="onCreateNew">{{ createLabel }}</a>
        </div>

        <p v-if="!plans.length" class="text-center my-2 mb-1">{{ emptyText }}</p>

        <div v-if="plans.length" class="table-responsive">
            <table class="table custom-table-padding mb-0 section-table">
                <thead>
                    <tr>
                        <th>Plan No</th>
                        <th>Title</th>
                        <th>Product</th>
                        <th>Schedule</th>
                        <th>Machine/Line</th>
                        <th class="text-right">Planned</th>
                        <th class="text-right">Produced</th>
                        <th class="text-right">Remaining</th>
                        <th>Progress</th>
                        <th>Status</th>
                        <th class="text-right">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="plan in plans" :key="plan.id">
                        <td class="fw-bold">{{ plan.plan_number }}</td>
                        <td>{{ displayValue(plan.title) }}</td>
                        <td>{{ displayValue(plan.product_name) }}</td>
                        <td>{{ scheduleLabel(plan) }}</td>
                        <td>{{ displayValue(plan.machine_name) }}</td>
                        <td class="text-right">{{ plan.qty }}</td>
                        <td class="text-right">{{ plan.produced_qty }}</td>
                        <td class="text-right">{{ plan.remaining_qty }}</td>
                        <td>
                            <div class="progress-track">
                                <div class="progress-fill" :style="{ width: progressWidth(plan.produced_qty, plan.qty) }"></div>
                            </div>
                        </td>
                        <td>
                            <span class="status-pill" :style="statusStyle(plan.status)">{{ statusLabel(plan.status) }}</span>
                        </td>
                        <td class="text-right">
                            <a href="javascript:void(0)" class="fw-bold" @click="onView(plan)">View</a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup>
import { getProductionPlanStatusColor } from '@/services/utils/global'

defineProps({
    plans: { type: Array, required: true },
    onView: { type: Function, required: true },
    onCreateNew: { type: Function, required: true },
    title: { type: String, default: 'Production Plans' },
    emptyText: { type: String, default: 'No production plan scheduled for this order yet.' },
    showCreate: { type: Boolean, default: true },
    createLabel: { type: String, default: '+ Add Production Plan' }
})

const displayValue = (value) => {
    if (!value) return '-'
    return value
}

const scheduleLabel = (plan) => {
    if (!plan.from_date_time_formatted) return '-'
    if (!plan.to_date_time_formatted) return plan.from_date_time_formatted
    return `${plan.from_date_time_formatted} - ${plan.to_date_time_formatted}`
}

const statusLabel = (status) => {
    if (!status) return ''
    return status.replace('_', ' ')
}

const statusStyle = (status) => {
    const color = getProductionPlanStatusColor(status)
    return { backgroundColor: color.bg, borderColor: color.border, color: color.text }
}

const progressWidth = (done, target) => {
    if (!target) return '0%'
    const ratio = (Number(done) / Number(target)) * 100
    if (ratio > 100) return '100%'
    return `${ratio.toFixed(0)}%`
}
</script>

<style scoped>
.section-card {
    overflow: hidden;
}
.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background-color: rgba(115, 103, 240, 0.08);
    border-bottom: 1px solid rgba(115, 103, 240, 0.16);
}
.section-title {
    font-size: 14px;
    font-weight: 600;
}
.row-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    margin-left: 8px;
    border-radius: 10px;
    background-color: rgba(115, 103, 240, 0.18);
    font-size: 11px;
    font-weight: 600;
}
.section-table thead th {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
}
.section-table th,
.section-table td {
    padding-right: 1rem;
}
.section-table th:last-child,
.section-table td:last-child {
    padding-right: 0.75rem;
}
.section-table tbody tr:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.02);
}
.fw-bold {
    font-weight: bold !important;
}
.status-pill {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 12px;
    border: 1px solid transparent;
    font-size: 12px;
    font-weight: 600;
    text-transform: capitalize;
    white-space: nowrap;
}
.progress-track {
    width: 100%;
    min-width: 90px;
    height: 6px;
    border-radius: 3px;
    background-color: #e5e7eb;
}
.progress-fill {
    height: 100%;
    border-radius: 3px;
    background-color: #22c55e;
}
</style>
